import {ReactElement, useEffect, useMemo} from 'react';
import { Property } from '../../../../contexts/properties/logic';
import { useGetCappedRenewalIncreaseLazyQuery, VersionType } from '../../../../__generated__/generated_types';
import { IVZHotTableRow } from '../../../../components/vz-ui/vz-hot-table/VZHotTable';
import { VZHotTableConfig } from '../../../../components/vz-ui/vz-hot-table/vz-hot-table-config';
import OperationalVZHotTable from '../OperationalVZHotTable';
import Handsontable from 'handsontable';
import { defaultSummaryColumnFunction } from '../../../../components/vz-ui/vz-hot-table/vz-hot-table-helpers';
import {getMonths} from '../../../../utils/months-helper';

interface IRenewalRateGrowthTableProps {
    property: Property;
    versionType: VersionType;
    budgetYear: number;
}

export default function RenewalRateGrowthTable({ property, versionType, budgetYear }: IRenewalRateGrowthTableProps): ReactElement {
    const [getCappedRenewalIncrease, {
        data: cappedRenewalIncreaseQueryData,
        loading: cappedRenewalIncreaseQueryLoading,
    }] = useGetCappedRenewalIncreaseLazyQuery({
        fetchPolicy: "no-cache"
    });

    useEffect(() => {
        getCappedRenewalIncrease({
            variables: {
                propertyId: property.id,
                thisYearM3: budgetYear - 4,
                thisYearM2: budgetYear - 3,
                thisYearM1: budgetYear - 2,
                thisYear: budgetYear - 1,
                nextYear: budgetYear,
                budgetYear: budgetYear
            }
        });
    }, [property]);

    function combineRfcstAndActuals(
        rfcstStartMonth: number,
        rfcstVals: {amounts: number[], percents: number[], inPlaceRents: number[]},
        actualsVals: {amounts: number[], percents: number[], inPlaceRents: number[]},
    ): {amounts: number[], percents: number[], inPlaceRents: number[]} {
        const amounts: number[] = [];
        const percents: number[] = [];
        const inPlaceRents: number[] = [];

        for (let i = 0; i < 12; i++) {
            if (i < rfcstStartMonth) {
                amounts.push(+(actualsVals.amounts[i] ?? 0));
                percents.push(+(actualsVals.percents[i] ?? 0) / 100);
                inPlaceRents.push(+(actualsVals.inPlaceRents[i] ?? 0));
            } else {
                amounts.push(+(rfcstVals.amounts[i] ?? 0));
                percents.push(+(rfcstVals.percents[i] ?? 0) / 100);
                inPlaceRents.push(+(rfcstVals.inPlaceRents[i] ?? 0));
            }
        }

        return {amounts, percents, inPlaceRents};
    }

    const {tableData, tableConfig} = useMemo<{tableData: IVZHotTableRow[], tableConfig: VZHotTableConfig}>(() => {
        const tableData: IVZHotTableRow[] = [];
        const tableConfig = new VZHotTableConfig();

        if (cappedRenewalIncreaseQueryLoading || !cappedRenewalIncreaseQueryData?.property) {
            return {tableData, tableConfig};
        }

        const thisYearActualsData = cappedRenewalIncreaseQueryData.property.thisYearActuals;
        const rfcstData = cappedRenewalIncreaseQueryData.property.thisYearReforecast;
        const bdgtData = cappedRenewalIncreaseQueryData.property.nextYearBudget;
        const unitTypes = cappedRenewalIncreaseQueryData.property.unitTypes;

        // build and add values for reforecast summary
        const {amounts: rfcstAmounts, percents: rfcstPercents, inPlaceRents: rfcstInPlaceRents} = combineRfcstAndActuals(property.reforecastStartMonthIndex, rfcstData, thisYearActualsData);
        const rfcstRows = [
            {
                label: "",
                values: rfcstAmounts,
                extraData: {
                    metric: "Tradeout $",
                },
            },
            {
                label: property.isAcquisitionProperty ? "Year 1" :  `Reforecast ${property.budgetYear - 1}`,
                values: rfcstPercents,
                extraData: {
                    metric: "Tradeout %",
                },
            },
            {
                label: "",
                values: rfcstInPlaceRents,
                extraData: {
                    metric: "In Place Rent",
                },
            },
        ];

        // add budget summary row, if on budget version
        if (versionType == VersionType.Budget) {
            const amounts = bdgtData.amounts.map(x => +x);
            const percents = bdgtData.percents.map(x => +x);
            const inPlaceRents = bdgtData.inPlaceRents.map(x => +x);

            tableData.push(
                {
                    label: "",
                    values: amounts,
                    extraData: {
                        metric: "Tradeout $",
                    },
                },
                {
                    label: `Budget ${property.budgetYear}`,
                    values: percents,
                    extraData: {
                        metric: "Tradeout %",
                    },
                },
                {
                    label: "",
                    values: inPlaceRents,
                    extraData: {
                        metric: "In Place Rent",
                    },
                }
            );
        } else {
            // otherwise, top should have the reforecast rows
            tableData.push(...rfcstRows);
        }

        const sortedUnitTypes = unitTypes.sort((a, b) => a.name.localeCompare(b.name));
        for (const unitType of sortedUnitTypes) {
            let amounts = [];
            let percents = [];
            let inPlaceRents = [];

            if (versionType == VersionType.Reforecast) {
                const combinedData = combineRfcstAndActuals(property.reforecastStartMonthIndex, unitType.thisYearReforecast, unitType.thisYearActuals);
                amounts = combinedData.amounts;
                percents = combinedData.percents;
                inPlaceRents = combinedData.inPlaceRents;
            } else {
                amounts = unitType.nextYearBudget.amounts.map(x => +x);
                percents = unitType.nextYearBudget.percents.map(x => +x);
                inPlaceRents = unitType.nextYearBudget.inPlaceRents.map(x => +x);
            }

            tableData.push({
                label: unitType.name,
                values: amounts,
                extraData: {
                    metric: "Tradeout $",
                },
            });

            tableData.push({
                label: unitType.name,
                values: percents,
                extraData: {
                    metric: "Tradeout %",
                },
            });

            tableData.push({
                label: unitType.name,
                values: inPlaceRents.map(x => Math.round(x)),
                extraData: {
                    metric: "In Place Rent",
                },
            });
        }

        // reforecast rows go at the bottom, with the historicals, if on budget version
        if (versionType == VersionType.Budget) {
            tableData.push(...rfcstRows);
        }

        const historicals = [
            cappedRenewalIncreaseQueryData.property.thisYearM1Actuals,
            cappedRenewalIncreaseQueryData.property.thisYearM2Actuals,
            cappedRenewalIncreaseQueryData.property.thisYearM3Actuals,
        ];
        const sortedHistoricals = historicals.sortByDescending("year");
        // validHistoricalRows is to decide how many rows should get the styling treatment for historicals
        // we start at 0 for reforecast, but 3 for budget since reforecast rows become historicals
        let validHistoricalRows = versionType == VersionType.Reforecast ? 0 : 3;
        for (const historical of sortedHistoricals) {
            const amounts = historical.amounts.map(x => +x);
            const percents = historical.percents.map(x => +x);
            const inPlaceRents = historical.inPlaceRents.map(x => +x);

            // if a historical doesnt have any meaningful data, skip it
            if ([...amounts, ...percents, ...inPlaceRents].every(x => x == null || x == 0)) {
                continue;
            }

            // to know how many rows should get the actuals grey background treatment
            validHistoricalRows += 3;

            tableData.push(
                {
                    label: "",
                    rowType: "historical",
                    values: amounts,
                    extraData: {
                        metric: "Tradeout $",
                    },
                },
                {
                    label: property.isAcquisitionProperty ? String(historical.year) : `Actuals ${historical.year}`,
                    rowType: "historical",
                    values: percents,
                    extraData: {
                        metric: "Tradeout %",
                    },
                },
                {
                    label: "",
                    rowType: "historical",
                    values: inPlaceRents,
                    extraData: {
                        metric: "In Place Rent",
                    },
                },
            );
        }

        tableConfig.tableSummaryFunction = "avg";

        // since this table has 3 rows per entry, this is to handle some of the tricky display issues
        // the modulus lets us track if we are on the first, second, or third row for each entry
        tableData.forEach((_row, index) => {
            const rowModulus = index % 3;
            const format = rowModulus == 1 ? "percent" : "currency";
            const isThickBorderBottom = rowModulus == 2 ? true : false;
            const hideBorderBottom = rowModulus == 2 ? false : true;
            const hideText = rowModulus == 1 ? false : true;

            tableConfig.applyToRow(index, {format, isThickBorderBottom});
            tableConfig.applyConfig({
                rowStart: index,
                rowEnd: index,
                colStart: 0,
                colEnd: 0,
                config: {hideBorderBottom, hideText},
            });
        });

        tableConfig.applyToColumn(0, {isRowHeader: true, format: "string", isBoldText: true});
        tableConfig.applyToColumn(1, {format: "string", isBoldText: true});
        tableConfig.applyToColumn(14, {isGreyBackground: true, isBoldText: true});
        tableConfig.applyToRowAndFollowing(tableData.length - validHistoricalRows, {isGreyBackground: true});

        return {tableData, tableConfig};
    }, [cappedRenewalIncreaseQueryData, cappedRenewalIncreaseQueryLoading]);

    return (
        <OperationalVZHotTable
            data={tableData}
            tableConfig={tableConfig}
            isDataLoading={cappedRenewalIncreaseQueryLoading}
            isSearchable
            colWidths={[80, 60]}
            colHeaders={["Unit Type", "Metric", ...getMonths(property), "Average"]}
            handsontableProps={{
                columns: [
                    {data: "label"},
                    {data: "extraData.metric"},
                    {data: `values.0`, type: "numeric"},
                    {data: `values.1`, type: "numeric"},
                    {data: `values.2`, type: "numeric"},
                    {data: `values.3`, type: "numeric"},
                    {data: `values.4`, type: "numeric"},
                    {data: `values.5`, type: "numeric"},
                    {data: `values.6`, type: "numeric"},
                    {data: `values.7`, type: "numeric"},
                    {data: `values.8`, type: "numeric"},
                    {data: `values.9`, type: "numeric"},
                    {data: `values.10`, type: "numeric"},
                    {data: `values.11`, type: "numeric"},
                    {data(row: Handsontable.RowObject) {
                        return defaultSummaryColumnFunction(tableData, tableConfig, row);
                    }},
                ],
            }}
        />
    );
}
